import React from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Stack
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';

const ProfessionalSection = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        backgroundColor: '#f5f5dc',
        py: 12,
        textAlign: 'center',
        color: 'black'
      }}
    >
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          component="h2"
          gutterBottom
          sx={{
            fontWeight: 700,
            mb: 3,
            fontSize: { xs: '2.8rem', sm: '3.2rem', md: '3.5rem' },
            color: '#212121',
            fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
            lineHeight: { xs: 1.1, md: 1.2 }
          }}
        >
          {t('professional.revolutionizeYourSalon')}
        </Typography>
        <Typography
          variant="h6"
          sx={{
            mb: 6,
            fontSize: '1.1rem',
            fontWeight: 400,
            color: '#666',
            maxWidth: 800,
            mx: 'auto',
            fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif'
          }}
        >
          {t('professional.allInOnePlatform')}
        </Typography>
        
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} justifyContent="center" alignItems="center">
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/auth?mode=signup')}
            sx={{
              background: 'linear-gradient(135deg, #212121 0%, #424242 50%, #212121 100%)',
              backgroundSize: '200% 100%',
              color: 'white',
              fontWeight: 700,
              fontSize: '1.1rem',
              px: 4,
              py: 2,
              borderRadius: 3,
              textTransform: 'none',
              position: 'relative',
              overflow: 'hidden',
              animation: 'gradientMove 4s ease-in-out infinite, gentlePulse 3s ease-in-out infinite',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '100%',
                height: '100%',
                background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
                animation: 'buttonShimmer 3s infinite',
                '@keyframes buttonShimmer': {
                  '0%': { left: '-100%' },
                  '100%': { left: '100%' }
                }
              },
              '@keyframes gradientMove': {
                '0%': { backgroundPosition: '0% 50%' },
                '50%': { backgroundPosition: '100% 50%' },
                '100%': { backgroundPosition: '0% 50%' }
              },
              '@keyframes gentlePulse': {
                '0%': {
                  transform: 'scale(1)',
                  boxShadow: '0 4px 15px rgba(33, 33, 33, 0.3)'
                },
                '50%': {
                  transform: 'scale(1.01)',
                  boxShadow: '0 8px 25px rgba(33, 33, 33, 0.4)'
                },
                '100%': {
                  transform: 'scale(1)',
                  boxShadow: '0 4px 15px rgba(33, 33, 33, 0.3)'
                }
              },
              '&:hover': {
                background: 'linear-gradient(135deg, #424242 0%, #616161 50%, #424242 100%)',
                transform: 'translateY(-2px) scale(1.03)',
                boxShadow: '0 12px 35px rgba(33, 33, 33, 0.5)',
                animation: 'none'
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            <Box component="span" sx={{ position: 'relative', zIndex: 1 }}>
              {t('professional.startFree')}
            </Box>
          </Button>
          <Button
            variant="outlined"
            size="large"
            onClick={() => document.getElementById('featured-salons').scrollIntoView({ behavior: 'smooth' })}
            sx={{
              color: '#212121',
              borderColor: '#212121',
              fontWeight: 'bold',
              fontSize: '1.1rem',
              px: 4,
              py: 2,
              borderRadius: 3,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#212121',
                color: 'white'
              }
            }}
          >
            {t('professional.discoverSalons')}
          </Button>
        </Stack>

        <Box
          sx={{
            mt: 4,
            p: { xs: 3, md: 2 },
            background: 'linear-gradient(135deg, rgba(33, 33, 33, 0.05) 0%, rgba(66, 66, 66, 0.03) 50%, rgba(33, 33, 33, 0.05) 100%)',
            borderRadius: 3,
            border: '1px solid rgba(33, 33, 33, 0.1)',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: '-100%',
              width: '100%',
              height: '100%',
              background: 'linear-gradient(90deg, transparent, rgba(245, 245, 220, 0.3), transparent)',
              animation: 'shimmer 3s infinite',
              '@keyframes shimmer': {
                '0%': { left: '-100%' },
                '100%': { left: '100%' }
              }
            }
          }}
        >
          <Typography
            variant="body1"
            sx={{
              color: '#666',
              fontSize: { xs: '1rem', md: '1rem' },
              fontWeight: 600,
              letterSpacing: '0.3px',
              background: 'linear-gradient(135deg, #666 0%, #8B7355 30%, #212121 50%, #8B7355 70%, #666 100%)',
              backgroundSize: '200% 100%',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              animation: 'textShine 3s ease-in-out infinite',
              position: 'relative',
              zIndex: 1,
              '@keyframes textShine': {
                '0%': { backgroundPosition: '0% 50%' },
                '50%': { backgroundPosition: '100% 50%' },
                '100%': { backgroundPosition: '0% 50%' }
              }
            }}
          >
            {t('professional.noCreditCard')}
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default ProfessionalSection;